import { useState, useRef, useCallback, useEffect } from 'react';
import { fileUploadApi, UploadedFileRecord } from '../../../services/api/fileUpload.api';

export interface UploadedFile {
  localId: string;
  fileId: string;
  name: string;
  extension: string;
  sizeBytes: number;
  status: 'uploading' | 'done' | 'error';
  errorMessage?: string;
}

const ACCEPTED_FORMATS = new Set(['pdf', 'docx', 'xlsx', 'csv', 'pptx', 'txt', 'jpg', 'jpeg', 'png']);
const MAX_FILES = 10;
const MAX_BYTES = 25 * 1024 * 1024;

let _counter = 0;
const nextLocalId = () => `fu-${Date.now()}-${++_counter}`;

// A file the backend already holds for this chat, as listed by GET
// /chat_file_upload/file.
const toUploadedFile = (f: UploadedFileRecord): UploadedFile => ({
  localId: nextLocalId(),
  fileId: f.file_id,
  name: f.original_filename,
  extension: f.file_extension,
  sizeBytes: f.file_size_bytes,
  status: 'done',
});

export const useFileUpload = () => {
  const [fileUploadMode, setFileUploadMode] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [validationError, setValidationError] = useState('');
  const validationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Whether the file-upload popup is currently visible. Separate from
  // fileUploadMode: turning file upload off always closes it, but closing the
  // popup (e.g. clicking the backdrop) leaves fileUploadMode — and the chat-bar
  // chip — on, so it can be reopened.
  const [isFileUploadModalOpen, setIsFileUploadModalOpen] = useState(false);

  const showValidationError = useCallback((msg: string) => {
    if (validationTimerRef.current) clearTimeout(validationTimerRef.current);
    setValidationError(msg);
    validationTimerRef.current = setTimeout(() => setValidationError(''), 5000);
  }, []);

  // The request listing the chat's existing files belongs to one opening of
  // the panel, or to one restore of a reopened chat. Opening it again,
  // turning upload off, leaving the chat, a newer restore or unmounting
  // cancels it, so a late list can never land in a later panel or another
  // chat.
  const listRequestRef = useRef<AbortController | null>(null);

  const cancelListing = useCallback(() => {
    listRequestRef.current?.abort();
    listRequestRef.current = null;
  }, []);

  // Cancels whatever is listing now and makes the new request the owner.
  const startListing = useCallback(() => {
    cancelListing();
    const request = new AbortController();
    listRequestRef.current = request;
    return request;
  }, [cancelListing]);

  useEffect(() => cancelListing, [cancelListing]);

  const fetchUploadedFiles = useCallback(async (sessionId: string, email: string) => {
    const request = startListing();
    try {
      const data = await fileUploadApi.listFiles(sessionId, request.signal);
      if (request.signal.aborted || !data) return;

      const listed = data.map(toUploadedFile);
      const listedIds = new Set(listed.map((f) => f.fileId));
      // The panel is usable while the list loads, so keep anything uploaded
      // meanwhile, after the chat's existing files. An upload that already
      // finished and is in the list shows once, as the listed file.
      setUploadedFiles((prev) => [
        ...listed,
        ...prev.filter((f) => !(f.status === 'done' && listedIds.has(f.fileId))),
      ]);
    } catch {
      // silently ignore — panel opens with empty list (a cancelled request
      // ends here too, and changes nothing)
    } finally {
      if (listRequestRef.current === request) listRequestRef.current = null;
    }
  }, [startListing]);

  const activateFileUpload = useCallback(async (sessionId: string, email: string) => {
    setFileUploadMode(true);
    setUploadedFiles([]);
    setIsFileUploadModalOpen(true);
    await fetchUploadedFiles(sessionId, email);
  }, [fetchUploadedFiles]);

  const deactivateFileUpload = useCallback(() => {
    cancelListing();
    setFileUploadMode(false);
    setUploadedFiles([]);
    setValidationError('');
    setIsFileUploadModalOpen(false);
  }, [cancelListing]);

  const resetForNewChat = useCallback(() => {
    cancelListing();
    setFileUploadMode(false);
    setUploadedFiles([]);
    setValidationError('');
    setIsFileUploadModalOpen(false);
  }, [cancelListing]);

  // Turns File Upload back on for a reopened chat whose latest message used
  // it (see chatSelectionsStorage) — without opening its popup. The files
  // live on the backend per chat, so they're listed again rather than
  // saved; if none are left (deleted or expired), File Upload stays off
  // instead of showing an empty chip. The list is a listing like any other
  // (see listRequestRef): turning File Upload on or off, or leaving the
  // chat, while it loads cancels the restore.
  const restoreFileUpload = useCallback(async (sessionId: string) => {
    const request = startListing();
    try {
      const data = await fileUploadApi.listFiles(sessionId, request.signal);
      // Cancelled (File Upload or the chat changed meanwhile), or no files
      // left: File Upload stays off.
      if (request.signal.aborted || !data || data.length === 0) return;
      setUploadedFiles(data.map(toUploadedFile));
      setFileUploadMode(true);
    } catch (e) {
      // A cancelled request rejects too; that's a skip, not a failure.
      if (request.signal.aborted) return;
      console.warn('[chat-selections] File Upload not restored — could not list the chat\'s files:', e);
    } finally {
      if (listRequestRef.current === request) listRequestRef.current = null;
    }
  }, [startListing]);

  // Validate, immediately mark as uploading, and POST each file in parallel.
  const uploadFilesDirectly = useCallback(async (
    files: File[],
    sessionId: string,
    email: string,
    currentFiles: UploadedFile[],
  ) => {
    // The limit is 10 files per upload ("at once"), not per chat: finished
    // files don't count, so more can be added in a later upload.
    const activeCount = currentFiles.filter((f) => f.status === 'uploading').length;

    const errors: string[] = [];
    const toUpload: Array<{ entry: UploadedFile; blob: File }> = [];
    let runningCount = activeCount;

    for (const file of files) {
      const ext = file.name.split('.').pop()?.toLowerCase() ?? '';

      if (!ACCEPTED_FORMATS.has(ext)) {
        errors.push(`We do not accept .${ext || 'unknown'} files.`);
        continue;
      }
      if (file.size > MAX_BYTES) {
        errors.push(`"${file.name}" is above 25 MB — try using Box for large files.`);
        continue;
      }
      if (runningCount >= MAX_FILES) {
        errors.push('Max 10 files allowed.');
        break;
      }

      const entry: UploadedFile = {
        localId: nextLocalId(),
        fileId: '',
        name: file.name,
        extension: ext,
        sizeBytes: file.size,
        status: 'uploading',
      };
      toUpload.push({ entry, blob: file });
      runningCount++;
    }

    if (errors.length > 0) {
      showValidationError(Array.from(new Set(errors)).join(' '));
    }

    if (toUpload.length === 0) return;

    // Add all as 'uploading' in one state update
    setUploadedFiles((prev) => [...prev, ...toUpload.map((t) => t.entry)]);

    // Upload all in parallel
    await Promise.all(
      toUpload.map(async ({ entry, blob }) => {
        try {
          const data = await fileUploadApi.uploadFile(blob, sessionId);
          if (data) {
            setUploadedFiles((prev) =>
              prev.map((f) =>
                f.localId === entry.localId
                  ? { ...f, status: 'done', fileId: data.file_id }
                  : f
              )
            );
          } else {
            scheduleErrorRemoval(entry.localId, `Please try uploading "${entry.name}" again.`);
          }
        } catch {
          scheduleErrorRemoval(entry.localId, `Please try uploading "${entry.name}" again.`);
        }
      })
    );
  }, [showValidationError]);

  const scheduleErrorRemoval = (localId: string, errorMessage: string) => {
    setUploadedFiles((prev) =>
      prev.map((f) =>
        f.localId === localId ? { ...f, status: 'error', errorMessage } : f
      )
    );
    setTimeout(() => {
      setUploadedFiles((prev) => prev.filter((f) => f.localId !== localId));
    }, 3000);
  };

  const deleteFile = useCallback(async (fileId: string, sessionId: string, email: string) => {
    try {
      const ok = await fileUploadApi.deleteFile(fileId, sessionId);
      if (ok) {
        setUploadedFiles((prev) => prev.filter((f) => f.fileId !== fileId));
      }
    } catch {
      // silently ignore
    }
  }, []);

  return {
    fileUploadMode,
    uploadedFiles,
    validationError,
    isFileUploadModalOpen,
    setIsFileUploadModalOpen,
    activateFileUpload,
    deactivateFileUpload,
    uploadFilesDirectly,
    deleteFile,
    resetForNewChat,
    restoreFileUpload,
  };
};
